/* eslint-disable */
import {
  CheckCircleOutlined, CloseCircleOutlined,
  EyeOutlined,
  HistoryOutlined, HourglassOutlined, MinusOutlined
} from '@ant-design/icons';
import {
  Button, Card, Col,
  message,
  Modal, Row, Space, Spin, Table,
  Tooltip,
  Typography
} from 'antd';
import { TableProps } from 'antd/lib';
import { AxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppSelector } from 'app/store';
import { getPayrollApproveListService } from 'common/services/extends/payrollPeriods';
import { ApprovesData } from 'common/services/extends/payrollPeriods/types';
import {
  handleStatusApprovedLabel, handleStatusLabel,
  LevelAPPRORVE, ROUTE_PATHS
} from 'common/utils/constant';
import {
  detectError, formatDateDDMMYYYY, formatDateTime,
  formatNumber,
  handleApproveAc,
  handleApproveAccount,
  handleApproveOP
} from 'common/utils/functions';
import roles, { getPermission } from 'configs/roles';

interface DataType {
  currentLevel: number;
  key: number;
  name: string;
  fromDate: string;
  toDate: string;
  status: number;
  approvedStatus: number;
  approvedAc: number;
  approvedOp: number;
  approvedAccount: number;
  approvesData?: ApprovesData[]
  accountant?: {
    id: number
    fullname: string
    idCardNumber: string
    department: string
  }
  operation?: {
    id: number
    fullname: string
    idCardNumber: string
    department: string
  }
  payrollData?: {
    id: number
    fullname: string
    idCardNumber: string
  },
  totalIncome?: number;
}

const TimeKeepingSalaryTable = () => {
  const { t } = useTranslation();

  const { defaultPageSize } = useAppSelector((state) => state.system);
  const rolesUser = useAppSelector((state) => state.auth.roles);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = searchParams.get('page');
  const projectId = searchParams.get('projectId');
  const staffId = searchParams.get('staffFullTimeId') || '';

  /* States */
  const [currentPage, setCurrentPage] = useState(Number(pageParam || 1));
  const [currentView, setCurrentView] = useState(defaultPageSize);
  const [activeData, setActiveData] = useState<DataType | null>(null);

  const queryKey = ['payroll-salary-list', currentPage, currentView];

  const { isLoading, data: payrollApproveListData } = useQuery(
    queryKey,
    () => getPayrollApproveListService({
      page: currentPage,
      limit: currentView,
      _projectId: projectId || '',
      _staffFullTimeId: staffId || '',
      _exceptPayrollPeriodStatusApprove: ''
    }),
    {
      enabled: !!(projectId && staffId) && getPermission(rolesUser, roles.APPROVE_SALARY_APPROVE_INDEX),
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          const status = error.response?.status;
          message.error(t(detectError(status)));
        }
      }
    }
  );

  const handleSetCurrentPage = (page: number) => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set('page', page.toString());
    setCurrentPage(page);
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleSetCurrentView = (view: number) => {
    setCurrentView(view);
  };

  useEffect(() => {
    if (pageParam) {
      setCurrentPage(Number(pageParam));
    } else {
      setCurrentPage(1);
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.set('page', '1');
      setSearchParams(newSearchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStatusApprovedIcon = (status: number) => {
    switch (status) {
      case 1:
        return <MinusOutlined style={{ fontSize: '24px', color: '#000' }} />;
      case 2:
        return <CloseCircleOutlined style={{ fontSize: '24px', color: '#FF3B30' }} />;
      case 3:
        return <HourglassOutlined style={{ fontSize: '24px', color: '#007aff' }} />;
      case 4:
        return (
          <CheckCircleOutlined style={{
            fontSize: '26px',
            backgroundColor: '#34c759',
            color: '#fff',
            borderRadius: '50%'
          }}
          />
        );
      default:
        return null;
    }
  };

  const StatusApprove = (level: number, statusApprove: boolean | null, numberApproveed?: number) => {
    if (level === 5) {
      return t('salary.reopen');
    }
    if (level === 1) {
      if (statusApprove) {
        return t('salary.calculateDone');
      }
      if (numberApproveed && numberApproveed > 1 && statusApprove === null) {
        return t('salary.needToRecalculateSalary');
      }
      return t('salary.calculateYet');
    }
    if (level !== 1) {
      if (statusApprove) {
        return t('salary.approved');
      }
      if (statusApprove === false) {
        return t('salary.refuse');
      }
      return t('salary.approving');
    }
    return '';
  };

  const handleStatusApprovedLabelModal = (level: number, statusApprove: boolean | null, numberApproveed?: number) => {
    if (level === 5) {
      return 'reopen';
    }
    if (level === 1) {
      if (statusApprove) {
        return 'approved';
      }
      if (numberApproveed && numberApproveed > 1 && statusApprove === null) {
        return 'waitingApprove';
      }
      return 'notApproved';
    }
    if (level !== 1) {
      if (statusApprove) {
        return 'approved';
      }
      if (statusApprove === false) {
        return 'refuse';
      }
      return 'approving';
    }
    return '';
  };

  const nameApprove = (level: number) => {
    if (level === 1) {
      return activeData?.payrollData?.fullname;
    }
    if (level === 2) {
      return activeData?.operation?.fullname || '';
    }
    if (level === 3) {
      return activeData?.accountant?.fullname || '';
    }
    if (level === 4) {
      return '';
    }
    if (level === 5) {
      return 'Super Admin';
    }
    return '';
  };

  const renderTextStatusSup = (record: DataType) => {
    if (record.approvedStatus === 4 || record.approvedStatus === 5) return t('salary.needToRecalculateSalary');
    if (record.status === 2) return t('salary.calculateDone');
    if (record.status === 1) return t('salary.calculateYet');
    return '';
  };

  const classStatusSup = (record: DataType) => {
    if (record.approvedStatus === 4 || record.approvedStatus === 5) return 'needToRecalculateSalary';
    if (record.status === 2) return 'yetDone';
    if (record.status === 1) return '';
    return '';
  };

  const columns: TableProps<DataType>['columns'] = [
    {
      title: t('system.name'),
      dataIndex: 'name',
      key: 'name',
      width: 230,
    },
    {
      title: t('salary.totalIncome'),
      dataIndex: 'totalIncome',
      key: 'totalIncome',
      align: 'center',
      width: 200,
      render: (data) => (
        <Typography.Text>
          {formatNumber(data)}
        </Typography.Text>
      ),
    },
    {
      title: t('program.fromDate'),
      dataIndex: 'fromDate',
      key: 'fromDate',
      render: (data) => (
        <Typography.Text>
          {formatDateDDMMYYYY(data, '/')}
        </Typography.Text>
      ),
    },
    {
      title: t('program.toDate'),
      dataIndex: 'toDate',
      key: 'toDate',
      render: (data) => (
        <Typography.Text>
          {formatDateDDMMYYYY(data, '/')}
        </Typography.Text>
      ),
    },
    {
      title: t('system.status'),
      dataIndex: 'status',
      key: 'status',
      width: 170,
      render: (data, record) => (
        <Typography.Text className={`salary-statusActive ${classStatusSup(record)}`}>
          {renderTextStatusSup(record)}
        </Typography.Text>
      ),
    },
    {
      title: t('salary.approvedStatus'),
      dataIndex: 'approvedStatus',
      key: 'approvedStatus',
      width: 170,
      render: (data) => (
        <Typography.Text className={`salary-statusApproved${` ${handleStatusApprovedLabel(data)}`}`}>
          {t(handleStatusLabel(data) || '')}
        </Typography.Text>
      ),
    },
    {
      title: t('salary.approvedOp'),
      dataIndex: 'approvedOp',
      key: 'approvedOp',
      align: 'center',
      render: (data) => (
        <>
          {handleStatusApprovedIcon(data)}
        </>
      ),
    },
    {
      title: t('salary.approvedAc'),
      dataIndex: 'approvedAc',
      key: 'approvedAc',
      align: 'center',
      render: (data) => (
        <>
          {handleStatusApprovedIcon(data)}
        </>
      ),
    },
    {
      title: t('salary.approvedAccountant'),
      dataIndex: 'approvedAccount',
      key: 'approvedAccount',
      align: 'center',
      render: (data) => (
        <>
          {handleStatusApprovedIcon(data)}
        </>
      ),
    },
    {
      title: t('system.action'),
      key: 'action',
      align: 'center',
      fixed: 'right',
      render: (data: DataType) => (
        <Space>
          <Tooltip title={t('salary.viewApproveDetail')}>
            <Button
              icon={<HistoryOutlined />}
              onClick={() => setActiveData(data)}
            />
          </Tooltip>
          <Tooltip title={t('salary.viewTableSalaryDetail')}>
            <Button
              icon={<EyeOutlined />}
              onClick={() => {
                const newSearchParams = new URLSearchParams(searchParams.toString());
                navigate(`${ROUTE_PATHS.SALARY_CALCULATE_TABLE_DETAIL}?${newSearchParams.toString()}&payrollPeriodId=${data.key}`);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const data: DataType[] = useMemo(() => {
    if (payrollApproveListData?.data) {
      return payrollApproveListData.data.map((item): DataType => ({
        currentLevel: item.currentLevel,
        key: item.payrollPeriodData.id,
        name: item.payrollPeriodData.name,
        fromDate: item.payrollPeriodData.startDate,
        toDate: item.payrollPeriodData.endDate,
        status: item.payrollPeriodData.status,
        approvedStatus: item.payrollPeriodData.statusApprove,
        approvesData: item.approvesData,
        payrollData: item.payrollData,
        accountant: item.accountant,
        operation: item.operation,
        approvedOp: handleApproveOP(item.currentLevel, item.approvesData),
        approvedAc: handleApproveAc(item.currentLevel, item.approvesData),
        approvedAccount: handleApproveAccount(item.currentLevel, item.approvesData),
        totalIncome: item.payrollPeriodData.totalIncome || 0,
      }));
    }
    return [];
  }, [payrollApproveListData]);

  return (
    <div className="time-keeping-table">
      <Spin spinning={isLoading}>
        <Card
          title={(
            <Typography.Title level={4}>
              {t('salary.periodSalary')}
            </Typography.Title>
          )}
        >
          <div className="t-pagetable_wrap">
            <Table<DataType>
              columns={columns}
              dataSource={data || []}
              pagination={{
                total: payrollApproveListData?.meta.total,
                pageSize: currentView,
                current: currentPage,
                onChange(page) {
                  handleSetCurrentPage(page);
                },
                onShowSizeChange(current) {
                  handleSetCurrentView(current);
                },
              }}
              scroll={{
                x: 1600
              }}
            />
          </div>
        </Card>
      </Spin>
      <Modal
        title={(
          <Typography.Title
            level={4}
          >
            {t('salary.approvedDetail')}
          </Typography.Title>
        )}
        open={!!activeData}
        footer={null}
        centered
        width={1000}
        onCancel={() => setActiveData(null)}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ width: '100%' }}>
            <Card
              title={(
                <Typography.Title
                  level={5}
                >
                  {t('salary.periodSalary')}
                </Typography.Title>
              )}
              style={{ width: '100%' }}
            >
              <Row gutter={16}>
                <Col xs={24} sm={8} className="u-mb-8">
                  <Typography.Text>
                    {t('salary.namePeriodSalary')}
                    {': '}
                  </Typography.Text>
                  <div className="u-mt-8">
                    <Typography.Text strong>
                      {activeData?.name || ''}
                    </Typography.Text>
                  </div>
                </Col>
                <Col xs={24} sm={8} className="u-mb-8">
                  <Typography.Text>
                    {t('program.fromDate')}
                    {': '}
                  </Typography.Text>
                  <div className="u-mt-8">
                    <Typography.Text strong>
                      {formatDateDDMMYYYY(activeData?.fromDate, '/')}
                    </Typography.Text>
                  </div>
                </Col>
                <Col xs={24} sm={8} className="u-mb-8">
                  <Typography.Text>
                    {t('program.toDate')}
                    {': '}
                  </Typography.Text>
                  <div className="u-mt-8">
                    <Typography.Text strong>
                      {formatDateDDMMYYYY(activeData?.toDate, '/')}
                    </Typography.Text>
                  </div>
                </Col>
              </Row>
            </Card>
            <Card
              title={(
                <Typography.Title
                  level={5}
                >
                  {t('salary.approvedDetail')}
                </Typography.Title>
              )}
              style={{ width: '100%', marginTop: '16px' }}
            >
              {
                activeData?.approvesData
                && activeData?.approvesData.length > 0
                && activeData?.approvesData.map((item) => (
                  <div key={item.id}>
                    <Row gutter={16} className="u-mt-8">
                      <Col xs={24} sm={8} className="u-mb-8">
                        <Typography.Text>
                          {t(LevelAPPRORVE[item.level - 1])}
                          {': '}
                        </Typography.Text>
                        <div className="u-mt-8">
                          <Typography.Text strong>
                            {nameApprove(item.level)}
                          </Typography.Text>
                        </div>
                      </Col>
                      <Col xs={24} sm={8} className="u-mb-8">
                        <Typography.Text>
                          {t('system.status')}
                          {': '}
                        </Typography.Text>
                        <div className="u-mt-8">
                          <Typography.Text strong className={`salary-modal-status${` ${handleStatusApprovedLabelModal(item.level, item.approve, activeData?.approvesData?.length)}`}`}>
                            {StatusApprove(item.level, item.approve, activeData?.approvesData?.length)}
                          </Typography.Text>
                        </div>
                      </Col>
                      <Col xs={24} sm={8} className="u-mb-8">
                        <Typography.Text>
                          {t('salary.at')}
                          {': '}
                        </Typography.Text>
                        <div className="u-mt-8">
                          <Typography.Text strong>
                            {(item.approve === true || item.approve === false || item.level === 1) ? (item.updatedAt ? formatDateTime(item.updatedAt) : '-') : '-'}
                          </Typography.Text>
                        </div>
                      </Col>
                    </Row>
                    {
                      item.level !== 1 && (
                        <Row>
                          <Col className="u-mb-8">
                            <Typography.Text>
                              {t('salary.reason')}
                              {': '}
                              <Typography.Text strong>
                                {item.note}
                              </Typography.Text>
                            </Typography.Text>
                          </Col>
                        </Row>
                      )
                    }
                  </div>
                ))
              }
            </Card>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TimeKeepingSalaryTable;
